import { type ReactNode, type CSSProperties } from 'react';

interface SkeletonProps {
  className?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

/**
 * Base skeleton component with shimmer animation
 */
export function Skeleton({ className = '', children, style }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse bg-gradient-to-r from-tg-secondary-bg via-gray-300/30 to-tg-secondary-bg bg-[length:200%_100%] animate-shimmer ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

/**
 * Skeleton for text lines
 */
export function SkeletonText({
  lines = 1,
  className = '',
  lastLineWidth = '75%',
}: {
  lines?: number;
  className?: string;
  lastLineWidth?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-4 rounded ${i === lines - 1 && lines > 1 ? '' : 'w-full'}`}
          style={i === lines - 1 && lines > 1 ? { width: lastLineWidth } : undefined}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton for circular elements (avatars, icons)
 */
export function SkeletonCircle({ size = 40, className = '' }: { size?: number; className?: string }) {
  return (
    <Skeleton
      className={`rounded-full ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * Skeleton for rectangular elements (images, cards)
 */
export function SkeletonBox({
  width,
  height,
  className = '',
}: {
  width?: number | string;
  height?: number | string;
  className?: string;
}) {
  return (
    <Skeleton
      className={`rounded-lg ${className}`}
      style={{ width, height }}
    />
  );
}

